// Die Tiere als Ansicht des Spielzustands: je `AnimalState` ein Blockmodell nach `species.model`,
// jedes Bild neu aus dem Zustand gesetzt. Position und Blick werden wie die Kamera zwischen dem
// vorigen und dem aktuellen Tick interpoliert; die Beine pendeln über die im Bild zurückgelegte
// Strecke, das Euter skaliert nach `isProductReady`. In den Three.js-Objekten liegt kein
// Spielzustand (Architektur-Regel 1), nur abgeleitete Anzeigewerte: die Pendelphase und die zuletzt
// gezeichnete Position, aus der die Strecke folgt. Modelle kommen aus Baukästen je Modellkennung;
// ein unbekanntes Modell wird ein schlichter Quader mit einmaliger Warnung.

import { BoxGeometry, Mesh, MeshLambertMaterial, type Object3D, type Scene } from 'three';
import { SPECIES, getSpecies, isProductReady, lerp, type AnimalState, type Species } from '@bauernhof/shared';
import { createKuhKit } from './animalModels/kuh.js';

/** Die Stellschrauben des Beinpendels. */
export const GAIT = {
  /** Größter Ausschlag eines Beins im Bogenmaß. */
  amplitude: 0.6,
  /** Zurückgelegte Strecke je vollem Pendelzyklus, in Blöcken. */
  strideLength: 1.1,
  /** Beim Stehen klingt der Ausschlag mit dieser Rate je Sekunde ab (e-Funktion). */
  settleRate: 12,
  /** Unterhalb dieses Ausschlags gilt das Bein als ruhig und steht exakt auf 0. */
  settleThreshold: 0.005,
} as const;

/** Skalierung des Euters: voll, wenn das Produkt bereit ist, sonst klein. */
export const UDDER_SCALE = { full: 1, empty: 0.45 } as const;

/**
 * Der Pendelwinkel der Beine. Beim Gehen ein Sinus über die zurückgelegte Strecke, damit die Beine
 * zum Boden passen und nicht zur Bildrate; beim Stehen klingt der vorige Ausschlag weich ab und
 * rastet unterhalb der Schwelle exakt auf 0 ein. Diagonal gegenüberliegende Beine bekommen im
 * Modell das entgegengesetzte Vorzeichen.
 */
export function legSwing(distanceWalked: number, moving: boolean, prevSwing: number, dt: number): number {
  if (moving) return GAIT.amplitude * Math.sin((2 * Math.PI * distanceWalked) / GAIT.strideLength);
  const rest = prevSwing * Math.exp(-GAIT.settleRate * Math.max(0, dt));
  return Math.abs(rest) < GAIT.settleThreshold ? 0 : rest;
}

/** Skalierung des Euters aus der Bereitschaft des Produkts. */
export function udderScale(ready: boolean): number {
  return ready ? UDDER_SCALE.full : UDDER_SCALE.empty;
}

/** Interpoliert zwei Winkel über den kürzesten Weg; das Ergebnis liegt auf der Seite von `a`. */
export function lerpAngle(a: number, b: number, t: number): number {
  const zweiPi = 2 * Math.PI;
  const diff = (((b - a + Math.PI) % zweiPi) + zweiPi) % zweiPi - Math.PI;
  return a + diff * t;
}

/** Fußpunkt und Blick eines Tiers, aus denen das Modell gesetzt wird. */
export interface AnimalPose {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

/**
 * Mischt den vorigen und den aktuellen Zustand (`alpha` 0 = vorher, 1 = jetzt). Ohne vorigen
 * Zustand (neues Tier, erster Tick) gilt der aktuelle. Der Blick nimmt den kürzesten Weg, auch
 * wenn ein Zustand einmal nicht stetig aus dem vorigen hervorgeht (später vom Server).
 */
export function interpolateAnimalPose(previous: AnimalState | undefined, current: AnimalState, alpha: number): AnimalPose {
  if (previous === undefined) {
    return { x: current.pos.x, y: current.pos.y, z: current.pos.z, yaw: current.yaw };
  }
  return {
    x: lerp(previous.pos.x, current.pos.x, alpha),
    y: lerp(previous.pos.y, current.pos.y, alpha),
    z: lerp(previous.pos.z, current.pos.z, alpha),
    yaw: lerpAngle(previous.yaw, current.yaw, alpha),
  };
}

/** Eigenständige Kopien der Tierzustände; die Simulation ersetzt `pos` und `target` je Tick. */
export function copyAnimalStates(animals: readonly AnimalState[]): AnimalState[] {
  return animals.map((animal) => ({
    ...animal,
    pos: { ...animal.pos },
    target: animal.target === null ? null : { ...animal.target },
  }));
}

/** Ein Blockmodell eines Tiers: Ursprung Fußpunkt-Mitte, Blick entlang −z, mit den animierbaren Teilen. */
export interface AnimalModel {
  object: Object3D;
  setLegSwing(swing: number): void;
  setUdderScale(scale: number): void;
  /** Gibt die Geometrien dieses Modells frei; gemeinsame Materialien gehören dem Baukasten. */
  dispose(): void;
}

/** Ein Baukasten je Modellkennung: hält Materialien und Texturen für alle Tiere desselben Modells. */
export interface ModelKit {
  create(species: Species): AnimalModel;
  dispose(): void;
}

export type ModelKitFactory = () => ModelKit;

/** Die bekannten Modelle nach `species.model`; ein neues Modell ist ein Eintrag hier plus ein Baukasten. */
export const MODEL_KITS: Readonly<Record<string, ModelKitFactory>> = {
  kuh: createKuhKit,
};

export interface AnimalViews {
  /**
   * Bringt die Szene auf den Stand von `animals`. `prevAnimals` ist der Zustand des vorigen Ticks
   * (Kopie, siehe `copyAnimalStates`) oder null, `alpha` der Anteil des laufenden Ticks, `tick`
   * der aktuelle Tick für die Bereitschaft des Produkts, `dt` die Zeit seit dem letzten Bild in
   * Sekunden für das Abklingen der Beine.
   */
  sync(animals: readonly AnimalState[], prevAnimals: readonly AnimalState[] | null, alpha: number, tick: number, dt: number): void;
  /** Entfernt alle Ansichten aus der Szene und gibt Geometrien, Materialien und Texturen frei. */
  dispose(): void;
}

export interface AnimalViewsOptions {
  /** Baukästen nach Modellkennung; Vorgabe `MODEL_KITS`. Tests geben eigene mit. */
  kits?: Readonly<Record<string, ModelKitFactory>>;
  /** Arten-Tabelle; Vorgabe `SPECIES`, nach dem Muster von ADR 0003. */
  species?: Readonly<Record<string, Species>>;
}

/** Eine Ansicht je Tier samt den abgeleiteten Anzeigewerten des Beinpendels. */
interface AnimalView {
  model: AnimalModel;
  modelId: string;
  /** Seit Beginn des laufenden Gangs gezeichnete Strecke in Blöcken; steht das Tier, wieder 0. */
  distance: number;
  swing: number;
  lastX: number;
  lastZ: number;
  /** Nummer des Bildes, in dem die Ansicht zuletzt gebraucht wurde; wer zurückbleibt, ist verschwunden. */
  seen: number;
}

export function createAnimalViews(scene: Scene, options: AnimalViewsOptions = {}): AnimalViews {
  const factories = options.kits ?? MODEL_KITS;
  const table = options.species ?? SPECIES;
  const kits = new Map<string, ModelKit>();
  const views = new Map<string, AnimalView>();
  const gewarnt = new Set<string>();
  let fallback: ModelKit | null = null;
  let bild = 0;

  const kitFor = (species: Species): ModelKit => {
    const vorhanden = kits.get(species.model);
    if (vorhanden !== undefined) return vorhanden;
    const factory = factories[species.model];
    let kit: ModelKit;
    if (factory !== undefined) {
      kit = factory();
    } else {
      if (!gewarnt.has(species.model)) {
        gewarnt.add(species.model);
        console.warn(`Kein Blockmodell „${species.model}“ für die Tierart „${species.name}“; zeige einen Quader.`);
      }
      fallback ??= createFallbackKit();
      kit = fallback;
    }
    kits.set(species.model, kit);
    return kit;
  };

  const entferne = (id: string, view: AnimalView): void => {
    scene.remove(view.model.object);
    view.model.dispose();
    views.delete(id);
  };

  const ansichtFor = (animal: AnimalState, species: Species, pose: AnimalPose): AnimalView => {
    const vorhanden = views.get(animal.id);
    if (vorhanden !== undefined && vorhanden.modelId === species.model) return vorhanden;
    if (vorhanden !== undefined) entferne(animal.id, vorhanden);
    const model = kitFor(species).create(species);
    scene.add(model.object);
    const view: AnimalView = { model, modelId: species.model, distance: 0, swing: 0, lastX: pose.x, lastZ: pose.z, seen: bild };
    views.set(animal.id, view);
    return view;
  };

  return {
    sync(animals, prevAnimals, alpha, tick, dt) {
      bild++;
      animals.forEach((animal, index) => {
        const species = getSpecies(animal.species, table);
        const pose = interpolateAnimalPose(vorigerZustand(prevAnimals, animal.id, index), animal, alpha);
        const view = ansichtFor(animal, species, pose);
        view.seen = bild;

        view.model.object.position.set(pose.x, pose.y, pose.z);
        view.model.object.rotation.y = pose.yaw;

        // Die Strecke folgt aus der gezeichneten Bewegung, damit die Beine zum Boden passen. Steht
        // das Tier, beginnt der nächste Gang bei Phase 0, wo der abgeklungene Ausschlag ohnehin liegt.
        view.distance = animal.moving ? view.distance + Math.hypot(pose.x - view.lastX, pose.z - view.lastZ) : 0;
        view.lastX = pose.x;
        view.lastZ = pose.z;
        view.swing = legSwing(view.distance, animal.moving, view.swing, dt);
        view.model.setLegSwing(view.swing);
        view.model.setUdderScale(udderScale(isProductReady(animal, tick)));
      });

      for (const [id, view] of views) {
        if (view.seen !== bild) entferne(id, view);
      }
    },
    dispose() {
      for (const [id, view] of views) entferne(id, view);
      for (const kit of new Set(kits.values())) kit.dispose();
      kits.clear();
      fallback = null;
    },
  };
}

/** Der vorige Zustand desselben Tiers: meist am selben Index, sonst über die id. */
function vorigerZustand(prevAnimals: readonly AnimalState[] | null, id: string, index: number): AnimalState | undefined {
  if (prevAnimals === null) return undefined;
  const kandidat = prevAnimals[index];
  if (kandidat?.id === id) return kandidat;
  return prevAnimals.find((animal) => animal.id === id);
}

/** Ein schlichter Quader in den Maßen der Art für Modelle ohne Baukasten. */
function createFallbackKit(): ModelKit {
  const material = new MeshLambertMaterial({ color: 0x9a8f80 });
  return {
    create(species) {
      // Die Geometrie wird nach oben verschoben, damit der Ursprung wie bei den Modellen der Fußpunkt ist.
      const geometry = new BoxGeometry(species.width, species.height, species.length).translate(0, species.height / 2, 0);
      const mesh = new Mesh(geometry, material);
      return {
        object: mesh,
        setLegSwing() {},
        setUdderScale() {},
        dispose() {
          geometry.dispose();
        },
      };
    },
    dispose() {
      material.dispose();
    },
  };
}
